/**
 * @fileoverview One-shot plan configuration component.
 * @summary Displays options for converting existing events or creating new one-shot events.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/components/OneShotConfiguration
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { SubscriptionType, SUBSCRIPTION_PLANS } from '@karaonow/shared'
import type { FC } from 'react'

interface Event {
  id: string
  name: string
  status: 'draft' | 'active' | 'completed' | 'cancelled' | 'hidden'
}

/**
 * Props for the OneShotConfiguration component.
 */
interface OneShotConfigurationProps {
  eventToConvert: Event | null
  freeEvents: Event[]
  selectedEventId: string | null
  onSelectEventId: (eventId: string) => void
  oneShotMode: 'convert' | 'create'
  setOneShotMode: (mode: 'convert' | 'create') => void
  onConvertEvent: (eventId: string) => void
  onCreateEvent: () => void
  isLoading?: boolean
  t: (key: string, options?: Record<string, any>) => string
}

/**
 * @summary Displays ONE_SHOT configuration options.
 * @param {OneShotConfigurationProps} props - Component props.
 * @returns {ReactElement} ONE_SHOT configuration component.
 */
const OneShotConfiguration: FC<OneShotConfigurationProps> = ({
  eventToConvert,
  freeEvents,
  selectedEventId,
  onSelectEventId,
  oneShotMode,
  setOneShotMode,
  onConvertEvent,
  onCreateEvent,
  isLoading = false,
  t,
}) => {
  const price = SUBSCRIPTION_PLANS[SubscriptionType.ONE_SHOT].priceEur

  return (
    <div className="mt-6 pt-6 border-t border-gray-200 space-y-4">
      <h4 className="font-semibold text-gray-900 mb-4">{t('subscription.chooseOption')}</h4>

      {/* Mode Selection Tabs */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setOneShotMode('convert')}
          disabled={isLoading}
          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
            oneShotMode === 'convert' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {t('subscription.convertEvent')}
        </button>
        <button
          onClick={() => setOneShotMode('create')}
          disabled={isLoading}
          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
            oneShotMode === 'create' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {t('subscription.createEvent')}
        </button>
      </div>

      {/* Mode Content */}
      {oneShotMode === 'convert' ? (
        // Convert Mode
        <div className="space-y-3">
          {eventToConvert ? (
            // Cas 1: Specific event from URL
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-gray-700 mb-3">
                L'événement <strong>{eventToConvert.name}</strong> sera converti en plan {t('subscription.planOneShot')}.
              </p>
              <button
                onClick={() => eventToConvert && onConvertEvent(eventToConvert.id)}
                disabled={isLoading}
                className={`w-full btn btn-primary text-sm flex items-center justify-center gap-2 ${isLoading ? 'opacity-70 cursor-wait' : 'disabled:opacity-50 disabled:cursor-not-allowed'}`}
              >
                {isLoading ? (
                  <>
                    <span className="inline-block animate-spin">⏳</span>
                    {t('common.loading')}
                  </>
                ) : (
                  <>
                    {t('subscription.convertThisEvent')} - {price}€
                  </>
                )}
              </button>
            </div>
          ) : freeEvents.length > 0 ? (
            // Cas 2: Select which free event to convert
            <>
              <p className="text-sm text-gray-700">{t('subscription.selectFreeEvent')}</p>
              <select
                value={selectedEventId || ''}
                onChange={(e) => onSelectEventId(e.target.value)}
                disabled={isLoading}
                className="input w-full text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">--</option>
                {freeEvents.map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.name} ({event.status === 'active' ? '🔴 Actif' : event.status})
                  </option>
                ))}
              </select>
              <button
                onClick={() => selectedEventId && onConvertEvent(selectedEventId)}
                disabled={!selectedEventId || isLoading}
                className={`w-full btn btn-primary text-sm flex items-center justify-center gap-2 ${isLoading ? 'opacity-70 cursor-wait' : 'disabled:opacity-50 disabled:cursor-not-allowed'}`}
              >
                {isLoading ? (
                  <>
                    <span className="inline-block animate-spin">⏳</span>
                    {t('common.loading')}
                  </>
                ) : (
                  <>
                    {t('subscription.convertEvent')} - {price}€
                  </>
                )}
              </button>
            </>
          ) : (
            // Cas 3: No free events
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
              <p className="text-sm text-gray-700">{t('subscription.noFreeEventsToConvert')}</p>
            </div>
          )}
        </div>
      ) : (
        // Create Mode
        <div className="space-y-3">
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <p className="text-sm text-gray-700 mb-3">{t('subscription.createDescription')}</p>
            <button
              onClick={onCreateEvent}
              disabled={isLoading}
              className={`w-full btn btn-primary text-sm whitespace-normal! wrap-break-word! py-3! px-4! flex items-center justify-center gap-2 ${isLoading ? 'opacity-70 cursor-wait flex-row' : 'flex-col disabled:opacity-50 disabled:cursor-not-allowed'}`}
            >
              {isLoading ? (
                <>
                  <span className="inline-block animate-spin">⏳</span>
                  {t('common.loading')}
                </>
              ) : (
                <>
                  <span className="block">{t('subscription.createNewOneShot')}</span>
                  <span className="block font-semibold mt-1">{price}€</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default OneShotConfiguration

